// Themed dialog shell, used anywhere the browser's own confirm/prompt boxes
// would otherwise appear.
//
// Those boxes can't be styled, say "localhost:3000 says", and look nothing
// like the rest of the shop — not what you want in front of someone about
// to delete an order.
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface Props {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Buttons, laid out right-aligned along the footer. */
  footer?: React.ReactNode;
}

const Modal: React.FC<Props> = ({ title, onClose, children, footer }) => {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Matching what a native dialog does: the first control is ready for a
    // keyboard, so Enter or Space answers without reaching for the mouse.
    panelRef.current?.querySelector('button')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg w-full max-w-md"
      >
        <div className="flex items-start justify-between border-b border-background-dark px-5 py-3">
          <h2 className="text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-light hover:bg-background-dark rounded p-1"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 text-sm text-text-light">{children}</div>

        {footer && (
          <div className="flex justify-end gap-2 border-t border-background-dark px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
